
import { useEffect, useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { debounce } from "lodash";

// Define interfaces for the payload types
interface RealtimePayload {
  new: Record<string, any>;
  old?: Record<string, any>;
  eventType: string;
}

export const useRealtimeMessages = (
  conversationId: string | null, 
  userId: string | null,
  isSellerMode: boolean = false
) => {
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeChannels, setActiveChannels] = useState<string[]>([]);
  
  // Initialize audio element once
  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.load(); // Pre-load the audio
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  
  // Play notification sound - separated for cleaner code
  const playNotificationSound = useCallback(() => {
    console.log('Attempting to play notification sound');
    if (audioRef.current) {
      audioRef.current.currentTime = 0; // Reset to start
      audioRef.current.play()
        .then(() => console.log('Notification sound played successfully'))
        .catch(err => console.log('Could not play notification sound:', err));
    } else {
      console.log('Audio element not initialized');
    }
  }, []);

  // Show notification toast - Use debounce to prevent too many toasts
  const showNotification = useCallback(
    debounce((title: string, description: string) => {
      console.log('Showing notification toast:', title, description);
      toast({
        title,
        description,
        duration: 5000, // Longer duration for better visibility
      });
    }, 500),
    []
  );

  // Update query cache to refresh unread counts
  const refreshUnreadCounts = useCallback(() => {
    console.log('Refreshing unread counts for mode:', isSellerMode ? 'seller' : 'user');
    queryClient.invalidateQueries({ queryKey: ['unread-messages', isSellerMode] });
    
    // Also refresh conversations lists
    const conversationsKey = isSellerMode ? ['cook-conversations'] : ['conversations'];
    queryClient.invalidateQueries({ queryKey: conversationsKey });
    
    // Also refresh the current conversation data if we're viewing one
    if (conversationId) {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
    }
  }, [queryClient, isSellerMode, conversationId]);

  const markConversationAsRead = useCallback(async () => {
    if (!conversationId || !userId) return;
    
    try {
      console.log(`${isSellerMode ? 'Seller' : 'Foodie'} marking conversation ${conversationId} as read`);
      
      const { error } = await supabase
        .from('conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId);
      
      if (error) {
        console.error('Error marking conversation as read:', error);
        return;
      }
      
      console.log('Successfully marked conversation as read');
      refreshUnreadCounts();
    } catch (error) {
      console.error('Unexpected error marking conversation as read:', error);
    }
  }, [conversationId, userId, isSellerMode, refreshUnreadCounts]);

  // Mark conversation as read when user views it
  useEffect(() => {
    if (conversationId && userId) {
      markConversationAsRead();
    }
  }, [conversationId, userId, markConversationAsRead]);

  useEffect(() => {
    if (!userId) {
      console.log('No user ID provided, skipping realtime setup');
      return;
    }
    
    console.log(`Setting up realtime messages for user ${userId}, conversation ${conversationId}, seller mode: ${isSellerMode}`);
    
    // Create stable channel names that won't change between renders
    const conversationsChannelName = `conv-changes-${userId}-${isSellerMode ? 'seller' : 'user'}`;
    const messagesChannelName = `msg-changes-${userId}-${isSellerMode ? 'seller' : 'user'}`;
    
    // Track active channels for cleanup
    setActiveChannels(prev => [...prev, conversationsChannelName, messagesChannelName]);
    
    console.log(`Creating channels: ${conversationsChannelName} and ${messagesChannelName}`);
    
    // Create channel for conversation changes
    const conversationsChannel = supabase
      .channel(conversationsChannelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: conversationId 
            ? `id=eq.${conversationId}` 
            : `or(cook_id.eq.${userId},foodie_id.eq.${userId})`
        },
        async (payload: RealtimePayload) => {
          console.log('Conversation updated:', payload);
          
          if (!payload.new || typeof payload.new !== 'object') return;
          
          // Log unread count for debugging
          console.log(`Conversation ${payload.new.id} unread count: ${payload.new.unread_count}`);
          
          // Immediately invalidate queries to ensure UI refreshes with new data
          refreshUnreadCounts();
          
          // If this is the currently viewed conversation, mark it as read
          if (conversationId && payload.new.id === conversationId) {
            markConversationAsRead();
          }
        }
      )
      .subscribe((status) => {
        console.log(`Conversations channel status: ${status}`);
        if (status === 'CHANNEL_ERROR') {
          console.error('Error in conversations channel, attempting to reconnect...');
          setTimeout(() => conversationsChannel.subscribe(), 1000);
        }
      });

    // Create channel for message changes
    const messagesChannel = supabase
      .channel(messagesChannelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: conversationId ? `conversation_id=eq.${conversationId}` : undefined
        },
        async (payload: RealtimePayload) => {
          console.log('New message received:', payload);
          
          if (!payload.new || typeof payload.new !== 'object') return;
          
          try {
            // Fetch conversation data to check if user should be notified
            const { data: conversationData } = await supabase
              .from('conversations')
              .select('*')
              .eq('id', payload.new.conversation_id)
              .single();
              
            if (!conversationData) {
              console.log('No conversation data found, skipping notification');
              return;
            }
            
            // Check if message belongs to current conversation
            const isCurrentConversation = conversationId && payload.new.conversation_id === conversationId;
            
            if (isCurrentConversation) {
              console.log('Message is for the current conversation');
              
              // Fetch complete message data with sender information
              const { data: messageWithSender } = await supabase
                .from('messages')
                .select(`
                  id,
                  content,
                  created_at,
                  sender_id,
                  sender:profiles!messages_sender_id_fkey(
                    id,
                    first_name,
                    last_name,
                    image_url
                  )
                `)
                .eq('id', payload.new.id)
                .single();

              if (messageWithSender) {
                // Update message cache with new message
                queryClient.setQueryData(['messages', conversationId], (oldData: any[] = []) => {
                  if (oldData.some(msg => msg.id === messageWithSender.id)) {
                    return oldData;
                  }
                  return [...oldData, messageWithSender];
                });
                
                // Scroll to bottom
                setTimeout(() => {
                  const threadEnd = document.getElementById('thread-end');
                  if (threadEnd) {
                    threadEnd.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
                
                // Mark as read if user is viewing the conversation and is the recipient (not the sender)
                if (userId && messageWithSender.sender_id !== userId) {
                  markConversationAsRead();
                  
                  // Play notification and show toast for messages from others when viewing the conversation
                  playNotificationSound();
                  showNotification(
                    "New message", 
                    "You received a new message in this conversation"
                  );
                }
              }
            } else {
              // Message is for a different conversation - update unread counts
              console.log('Message is for a different conversation');
              
              // Determine if user is recipient
              const isUserRecipient = isSellerMode 
                ? conversationData.cook_id === userId && payload.new.sender_id !== userId
                : conversationData.foodie_id === userId && payload.new.sender_id !== userId;
              
              if (isUserRecipient) {
                // This message is for the user, play notification
                playNotificationSound();
                showNotification(
                  "New message", 
                  "You received a new message in another conversation"
                );
                
                // Force refresh unread counts in UI
                refreshUnreadCounts();
              }
            }
          } catch (error) {
            console.error('Error processing new message:', error);
          }
        }
      )
      .subscribe((status) => {
        console.log(`Messages channel status: ${status}`);
        if (status === 'CHANNEL_ERROR') {
          console.error('Error in messages channel, attempting to reconnect...');
          setTimeout(() => messagesChannel.subscribe(), 1000);
        }
      });

    // Cleanup function to remove channels
    return () => {
      console.log("Cleaning up real-time channels");
      supabase.removeChannel(conversationsChannel);
      supabase.removeChannel(messagesChannel);
      
      // Remove from active channels list
      setActiveChannels(prev => prev.filter(
        ch => ch !== conversationsChannelName && ch !== messagesChannelName
      ));
    };
  }, [
    conversationId, 
    userId, 
    isSellerMode, 
    queryClient, 
    playNotificationSound, 
    showNotification,
    refreshUnreadCounts,
    markConversationAsRead
  ]);

  // Update unread counts periodically to ensure they stay accurate
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (userId) {
        refreshUnreadCounts();
      }
    }, 5000); // Every 5 seconds
    
    return () => clearInterval(intervalId);
  }, [userId, refreshUnreadCounts]);

  return {
    refreshUnreadCounts,
    markConversationAsRead
  };
};
